import {
  Component,
  OnInit,
  Input,
  ViewContainerRef,
  ViewChild,
  ComponentRef,
  Type,
  ComponentFactoryResolver,
  Output,
  EventEmitter,
  AfterViewInit,
  ChangeDetectorRef
} from '@angular/core';
import { DataEntity } from '../../../../models/data-entities';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { DataEntityFactory, ValidationService, WorkflowContextService } from '../../../../services';
import { Activity, ActivityModel } from '../../../../models/activities';
import { DataEntityViewComponent } from '../data-entity-view/data-entity-view.component';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { LayoutService } from 'src/app/services/layout.service';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';

@Component({
  selector: 'wm-data-entity-input',
  templateUrl: './data-entity-input.component.html',
  styleUrls: ['./data-entity-input.component.css'],
  standalone: false
})
export class DataEntityInputComponent implements OnInit, AfterViewInit {
  @Input() entity: DataEntity;
  @Input() form: UntypedFormGroup;
  @Input() applicationId: string;
  @Input() activity: Activity<ActivityModel>;
  @Input() isPreview: boolean;
  @Input() showLabel = true;
  @Input() orientation: 'horizontal' | 'vertical' = 'vertical';

  @Output() calculateActivity: EventEmitter<{
    templateCode: string;
    calculateFormula?: boolean;
    visReq?: boolean;
  }> = new EventEmitter();

  @ViewChild('entityContainer', { read: ViewContainerRef, static: true })
  entityContainer: ViewContainerRef;
  inputComponentRef: ComponentRef<DataEntityInputComponent>;
  viewComponentRef: ComponentRef<DataEntityViewComponent>;
  activities: string;
  templateForPermitForm: boolean;
  // valueUpdatedSubscriber: Subscription;

  constructor(public layoutService: LayoutService, private componentFactoryResolver: ComponentFactoryResolver, private _ref: ChangeDetectorRef, context: WorkflowContextService) {
    //added by priya 05-01-2026
    layoutService.useNewLayout$.subscribe((useNewLayout) => {
      this.templateForPermitForm = useNewLayout;
    });
    //end by priya 05-01-2026
  }

  async persistChildComponent?() {
    if (this.inputComponentRef) {
      // If the entity is not visible and we aren't calculating, set its value to null
      // to ensure it does not persist any value when hidden.
      // if (!this.entity.isVisible && this.form.controls[this.calculatingFormControlName] && this.form.controls[this.calculatingFormControlName].valid) {
      //   this.entity.value = null;
      // }
      return this.inputComponentRef.instance.persistChildComponent();
    } else {
      return new Promise(resolve => {
        resolve(null);
      });
    }
  }
  ngOnInit() {
    if (this.entity && this.entity.completedDependentActivtyInfo) {
      this.activities = this.entity.completedDependentActivtyInfo
        .map(m => m.activityLabel)
        .join(',');
    }
    this.loadEntity();

    this.entity.isCalculating = false;
  }

  unlockEntity() {
    this.entity.isLocked = false;
    this.loadEntity();
  }

  ngAfterViewInit(): void {
    // watch for changes that are included in the formula for the data entity if there are some on the same activity
    if ((this.entity.sameActivityFormulaDataEntities || []).length > 0) {
      // check to see if there are changes ever second and calculate new value if there are changes
      this.watchForCalculation();
    }

    if (
      !this.entity.isVisible &&
      this.form &&
      this.form.controls[this.entity.templateCode]
    ) {
      this.form.controls[this.entity.templateCode].disable();
    } else if (this.form && this.form.controls[this.entity.templateCode]) {
      this.form.controls[this.entity.templateCode].enable();
    }
  }

  fireCalculation() {
    if (!this.entity.isLocked) {
      const activityEntities = this.activity.model.getEntities();

      this.entity.evaluateIsVisible(activityEntities, this.activity).subscribe(isVisible => {
        if (!isVisible) {
          this.form.controls[this.entity.templateCode].disable();
        } else {
          this.form.controls[this.entity.templateCode].enable();
        }
      });

      this.entity.evaluateIsRequired(activityEntities, this.activity).subscribe(isRequired => {
        if (isRequired) {
          this.form
            .controls[this.entity.templateCode]
            .setValidators(
              this.entity.getValidators()
            );
        } else {
          this.form.controls[this.entity.templateCode].clearValidators();
        }
        // Added by priya on 17-08-2026
        //  Recalculate the control's validation state after updating validators

        this.form.controls[this.entity.templateCode]
          .updateValueAndValidity();
        //end by priya on 17-08-2026
        this._ref.detectChanges();
      });
    }
  }

  cancelCalculation() {
    if (this.inputComponentRef) {
      this.inputComponentRef.instance.cancelCalculation();
    }
  }

  watchForCalculation() {
    const calcValue = changes => {
      this.fireCalculation();
    };

    setTimeout(() => {
      // watch for changes on the data entity that are used in the formula
      this.entity.sameActivityFormulaDataEntities.forEach((value, idx) => {
        if (this.form.controls[value]) {
          this.form.controls[value].valueChanges
            .pipe(distinctUntilChanged())
            .subscribe(calcValue);
        }
      });
    }, 100);
  }

  watchForExternalSourceChanges() {
    // console.log('Watching for external source changes for entity:', this.entity);
    // check if this entity has an external data source configured
    if (this.entity.externalDataSource && this.entity.externalDataSource.sourceConfig) {
      const sourceConfig = this.entity.externalDataSource.sourceConfig as any;

      // For custom list external source, watch the source list data entity
      if (sourceConfig.selectedOption && sourceConfig.selectedOption.listDataEntityTemplateCode) {
        const sourceEntityTemplateCode = sourceConfig.selectedOption.listDataEntityTemplateCode;

        setTimeout(() => {
          if (this.form.controls[sourceEntityTemplateCode]) {
            this.form.controls[sourceEntityTemplateCode].valueChanges
              .pipe(distinctUntilChanged())
              .subscribe(changes => {
                // When the source list entity changes, trigger a calculation to populate the external value
                this.fireCalculation();
              });
          }
        }, 100);
      }

      // For registration external source, watch for changes to any mapped source entities
      if (sourceConfig.selectedMappings && sourceConfig.selectedMappings.mappings) {
        const sourceTemplateCodes = sourceConfig.selectedMappings.mappings
          .map(m => m.sourceEntityTemplateCode)
          .filter(code => code); // filter out null/undefined

        setTimeout(() => {
          sourceTemplateCodes.forEach(templateCode => {
            if (this.form.controls[templateCode]) {
              this.form.controls[templateCode].valueChanges
                .pipe(distinctUntilChanged())
                .subscribe(changes => {
                  this.fireCalculation();
                });
            }
          });
        }, 100);
      }

      // For applicant data external source, watch the applicant selection
      if (sourceConfig.selectedOption && sourceConfig.selectedOption.field) {
        // Applicant data is typically populated from application-level data
        // so we don't need to watch form controls here
      }

      // For parcel search external source, watch the parcel selection
      if (sourceConfig.selectedOption && sourceConfig.selectedOption.parcelField) {
        // Parcel data is typically populated from parcel search results
        // so we don't need to watch form controls here
      }
    }
  }

  ngOnDestroy(): void {
  }

  loadEntity() {
    const code = this.entity.dataEntityTypeCode;
    if (!(this.entity instanceof DataEntity)) {
      this.entity = DataEntityFactory.createDataEntity(code, this.entity);
    }
    if (this.entityContainer) {
      this.entityContainer.clear();

      if (this.entity.isLocked) {
        const viewCompType: Type<DataEntityViewComponent> = DataEntityFactory.createViewComponent(
          this.entity,
          this.entity
        );

        if (viewCompType && this.entityContainer) {
          this.viewComponentRef = this.entityContainer.createComponent(
            viewCompType
          );
          this.viewComponentRef.instance.entity = this.entity;
          this.viewComponentRef.instance.activity = this.activity;
          this.viewComponentRef.instance.showLabel = this.showLabel;
          this.viewComponentRef.instance.orientation = this.orientation;
          this.viewComponentRef.instance.persistChildComponent = this
            .viewComponentRef.instance.persistChildComponent
            ? this.viewComponentRef.instance.persistChildComponent
            : async function () {
              return new Promise(resolve => {
                resolve(null);
              });
            };
        }
        this.form.removeControl(this.entity.templateCode);
      } else {
        if (this.form && !this.form.controls[this.entity.templateCode]) {
          this.form.addControl(
            this.entity.templateCode,
            ValidationService.createEntityValidationGroup(this.entity)
          );
        }

        const compType: Type<DataEntityInputComponent> = DataEntityFactory.createInputComponent(
          this.entity,
          null
        );
        if (compType && this.entityContainer) {
          this.inputComponentRef = this.entityContainer.createComponent(
            compType
          );
          this.inputComponentRef.instance.form = this.form;
          this.inputComponentRef.instance.entity = this.entity;
          this.inputComponentRef.instance.applicationId = this.applicationId;
          this.inputComponentRef.instance.activity = this.activity;
          this.inputComponentRef.instance.showLabel = this.showLabel;
          this.inputComponentRef.instance.orientation = this.orientation;
          this.inputComponentRef.instance.calculateActivity.subscribe(e => {
            this.calculateActivity.emit(e);
          });
          this.inputComponentRef.instance.isPreview = this.isPreview;
          this.inputComponentRef.instance.persistChildComponent = this
            .inputComponentRef.instance.persistChildComponent
            ? this.inputComponentRef.instance.persistChildComponent
            : async function () {
              return new Promise(resolve => {
                resolve(null);
              });
            };
        }
      }
    }
  }
  unlockModalData: CommonConfirmationModalData = {
    open: false,
    modalDescp: 'Edit Entity Value',
    modalSubDescp: 'Since the application is completed, we will not be automatically recalculating formulas or regenerating documents that are using this value.',
    yesNoBtn: true,
    okBtn: false,
    closeBtn: false,
    inTime: 300,
    outTime: 200,
    status: 'default'
  };
  onUnlockClick = () => {
    return {
      text: 'UNLOCK',
      onClick: () => {
        this.unlockModalData = {
          ...this.unlockModalData,
          open: true
        }

      }
    }
  }
  handleUnlockModalAccepted() {
    this.unlockEntity()
  }

  handleUnlockModalModalRejected() {

  }

  shouldShowControlMessage() {
    const code = this.entity?.dataEntityTypeCode?.toLowerCase() || '';
    return !(
      this.templateForPermitForm &&
      (code.includes('document') || code.includes('list'))
    );
  }
}
